import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const DoVerifyPage = () => {
  const navigate = useNavigate();

  // ── Tab ───────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('do');

  // ── DO Verify state ───────────────────────────────────────────────────────
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);

  // ── TrustHathi Verify state ───────────────────────────────────────────────
  const [trustCode, setTrustCode] = useState('');
  const [trustLoading, setTrustLoading] = useState(false);
  const [trustResult, setTrustResult] = useState(null);
  const [trustSearched, setTrustSearched] = useState(false);

  // ── DO Verify handler ─────────────────────────────────────────────────────
  const handleVerify = async () => {
    const code = passcode?.trim()?.toUpperCase();
    if (!code || code?.length < 6) return;
    setLoading(true);
    setSearched(false);
    setResult(null);
    try {
      const { data, error } = await supabase
        ?.from('buyer_bookings')
        ?.select('readable_booking_id, factory_name, grade, quantity, booking_date, status, do_number')
        ?.eq('do_verification_code', code)
        ?.maybeSingle();
      if (error) throw error;
      setResult(data ? { found: true, data } : { found: false });
    } catch (err) {
      setResult({ found: false });
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  // ── TrustHathi Verify handler ─────────────────────────────────────────────
  const handleTrustVerify = async () => {
    const code = trustCode?.trim()?.toUpperCase();
    if (!code || code?.length < 6) return;
    setTrustLoading(true);
    setTrustSearched(false);
    setTrustResult(null);
    try {
      const { data, error } = await supabase
        .from('vehicle_compliance_certs')
        .select('vehicle_number, driver_name, rc_status, rc_owner_name, rc_vh_class_desc, rc_insurance_upto, rc_fit_upto, rc_permit_valid_upto, rc_blacklist_status, dl_status, dl_tr_val_upto, dl_covdesc, overall_result, fail_reasons, created_at')
        .eq('verify_code', code)
        .maybeSingle();
      if (error) throw error;
      setTrustResult(data ? { found: true, data } : { found: false });
    } catch (err) {
      setTrustResult({ found: false });
    } finally {
      setTrustLoading(false);
      setTrustSearched(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') activeTab === 'do' ? handleVerify() : handleTrustVerify();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (err) { return dateStr; }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === 'approved' || s === 'delivered' || s === 'completed') return 'text-green-300 bg-green-900/40 border border-green-500/40';
    if (s === 'pending') return 'text-amber-300 bg-amber-900/40 border border-amber-500/40';
    if (s === 'cancelled' || s === 'rejected') return 'text-red-300 bg-red-900/40 border border-red-500/40';
    return 'text-slate-300 bg-slate-700/40 border border-slate-500/40';
  };

  const isGoodStatus = (val) => {
    const v = (val || '').toUpperCase();
    return v === 'ACTIVE' || v.includes('NOT BLACKLISTED') || v === 'CLEAR' || v === 'PASS';
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(160deg, #0f1f35 0%, #1e4a72 55%, #0f1f35 100%)', animation: 'pageFadeIn 0.5s ease both' }}
    >
      {/* Dot pattern */}
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.045) 1px, transparent 0)', backgroundSize: '28px 28px', zIndex: 0 }} />

      {/* Ambient blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #d97706 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-8" style={{ background: 'radial-gradient(circle, #1e4a72 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />
      </div>

      {/* Navbar */}
      <header className="sticky top-0 z-50 w-full border-b border-white/10 backdrop-blur-md" style={{ backgroundColor: 'rgba(15,31,53,0.92)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/10 flex-shrink-0 border border-white/10 shadow-lg shadow-amber-500/10">
                <img src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png" alt="SugarHathi logo" className="w-full h-full object-contain" />
              </div>
              <div className="hidden sm:block">
                <span className="text-lg font-black text-white tracking-tight group-hover:text-amber-400 transition-colors">SugarHathi</span>
                <div className="text-[10px] text-slate-400 leading-none">Factory-Direct Sugar Platform</div>
              </div>
            </button>
            <nav className="flex items-center gap-2">
              <button onClick={() => navigate('/')} className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-white/10 transition-all">🏠 Home</button>
              <button onClick={() => navigate('/sugar-marketplace-dashboard')} className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-white/10 transition-all">🛒 Browse Sugar Offers</button>
              <button onClick={() => navigate('/login-authentication')} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all border border-white/20 hover:border-amber-400/60 hover:text-amber-400">Sign In →</button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative z-10 py-12 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-40 rounded-full opacity-15" style={{ background: 'radial-gradient(ellipse, #d97706 0%, transparent 70%)' }} />
        </div>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-5 border" style={{ background: 'rgba(217,119,6,0.15)', borderColor: 'rgba(217,119,6,0.6)', color: '#fbbf24' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            DOCUMENT VERIFICATION
          </div>
          <h1 className="text-white font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-3">
            {activeTab === 'do' ? 'Verify Delivery Order' : 'Verify TrustHathi Certificate'}
          </h1>
          <p className="text-base sm:text-lg font-medium" style={{ color: '#d97706' }}>
            {activeTab === 'do'
              ? 'Enter your DO Passcode to verify authenticity of this document'
              : 'Enter your TrustHathi verify code to confirm vehicle compliance'}
          </p>
        </div>
      </div>

      {/* Amber accent bar */}
      <div className="relative z-10 h-[2px] w-full" style={{ background: 'linear-gradient(90deg, transparent, #d97706, #fbbf24, #d97706, transparent)' }} />

      {/* Tab Switcher */}
      <div className="relative z-10 flex justify-center mt-8 px-4">
        <div className="flex rounded-xl overflow-hidden border border-white/10" style={{ background: 'rgba(20,45,75,0.6)' }}>
          <button
            onClick={() => setActiveTab('do')}
            className="flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all"
            style={{
              background: activeTab === 'do' ? 'rgba(217,119,6,0.25)' : 'transparent',
              color: activeTab === 'do' ? '#fbbf24' : '#94a3b8',
              borderBottom: activeTab === 'do' ? '2px solid #d97706' : '2px solid transparent',
            }}
          >
            📄 Delivery Order
          </button>
          <button
            onClick={() => setActiveTab('trust')}
            className="flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all"
            style={{
              background: activeTab === 'trust' ? 'rgba(217,119,6,0.25)' : 'transparent',
              color: activeTab === 'trust' ? '#fbbf24' : '#94a3b8',
              borderBottom: activeTab === 'trust' ? '2px solid #d97706' : '2px solid transparent',
            }}
          >
            🛡️ TrustHathi Certificate
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center px-4 py-10">

        {/* ── DO TAB ── */}
        {activeTab === 'do' && (
          <>
            {/* Input Card */}
            <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'rgba(20,45,75,0.85)', border: '1px solid rgba(217,119,6,0.25)', boxShadow: '0 0 40px rgba(217,119,6,0.12), 0 20px 60px rgba(0,0,0,0.4)', backdropFilter: 'blur(12px)' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: 'rgba(217,119,6,0.2)', background: 'rgba(217,119,6,0.06)' }}>
                <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#d97706' }}>DO PASSCODE</p>
                <p className="text-slate-400 text-xs mt-0.5">Enter the 10-character code printed on your Delivery Order</p>
              </div>
              <div className="px-6 py-6">
                <input
                  type="text"
                  value={passcode}
                  onChange={(e) => setPasscode(e?.target?.value?.toUpperCase())}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. FYEFS68JZR"
                  maxLength={20}
                  className="w-full px-4 py-4 rounded-xl text-base font-black tracking-[0.2em] uppercase focus:outline-none transition-all placeholder:font-normal placeholder:tracking-normal"
                  style={{ backgroundColor: '#ffffff', color: '#374151', border: `2px solid ${passcode ? '#d97706' : '#e5e7eb'}`, caretColor: '#d97706' }}
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="text-xs mt-2 ml-1" style={{ color: '#9ca3af' }}>Auto-converts to uppercase · Press Enter to verify</p>
                <button
                  onClick={handleVerify}
                  disabled={loading || !passcode?.trim()}
                  className="mt-5 w-full py-4 rounded-xl font-black text-base tracking-widest uppercase transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
                  style={{ backgroundColor: loading || !passcode?.trim() ? 'rgba(217,119,6,0.5)' : '#d97706', color: '#1e4a72', boxShadow: passcode && !loading ? '0 4px 24px rgba(217,119,6,0.45)' : 'none' }}
                  onMouseEnter={(e) => { if (!loading && passcode?.trim()) e.currentTarget.style.backgroundColor = '#f59e0b'; }}
                  onMouseLeave={(e) => { if (!loading && passcode?.trim()) e.currentTarget.style.backgroundColor = '#d97706'; }}
                >
                  {loading ? (<><svg className="animate-spin h-5 w-5" style={{ color: '#1e4a72' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>Verifying…</>) : (<>🔍 VERIFY NOW</>)}
                </button>
              </div>
            </div>

            {/* DO Result */}
            {searched && result && (
              <div className="w-full max-w-md mt-6 rounded-2xl overflow-hidden shadow-2xl" style={{ animation: result?.found ? 'fadeInUp 0.4s ease both' : 'shakeIn 0.5s ease both', border: result?.found ? '2px solid rgba(34,197,94,0.5)' : '2px solid rgba(239,68,68,0.5)', background: 'rgba(20,45,75,0.9)', boxShadow: result?.found ? '0 0 30px rgba(34,197,94,0.15), 0 20px 50px rgba(0,0,0,0.4)' : '0 0 30px rgba(239,68,68,0.15), 0 20px 50px rgba(0,0,0,0.4)', backdropFilter: 'blur(12px)' }}>
                {result?.found ? (
                  <>
                    <div className="px-6 py-4 flex items-center gap-3 border-b" style={{ background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.2)' }}>
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', animation: 'checkPop 0.4s 0.2s ease both' }}>✅</div>
                      <div>
                        <p className="font-black text-green-400 text-base">Delivery Order Verified</p>
                        <p className="text-xs" style={{ color: 'rgba(74,222,128,0.7)' }}>Authentic document · Verified by SugarHathi</p>
                      </div>
                    </div>
                    <div className="px-6 py-5 space-y-3">
                      {[
                        { label: 'DO Number', value: result?.data?.do_number || '—', highlight: true },
                        { label: 'Booking ID', value: result?.data?.readable_booking_id || '—' },
                        { label: 'Factory Name', value: result?.data?.factory_name || '—' },
                        { label: 'Grade', value: result?.data?.grade || '—' },
                        { label: 'Quantity', value: result?.data?.quantity ? `${result?.data?.quantity} MT` : '—' },
                        { label: 'Booking Date', value: formatDate(result?.data?.booking_date) },
                      ]?.map((row, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b last:border-0" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                          <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#94a3b8' }}>{row?.label}</span>
                          <span className={`text-sm font-bold text-right max-w-[55%] ${row?.highlight ? '' : 'text-white'}`} style={row?.highlight ? { color: '#fbbf24' } : {}}>{row?.value}</span>
                        </div>
                      ))}
                      {result?.data?.status && (
                        <div className="flex justify-between items-center py-2">
                          <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#94a3b8' }}>Status</span>
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${getStatusColor(result?.data?.status)}`}>{result?.data?.status?.toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-6 py-4 flex items-center gap-3 border-b" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.2)' }}>
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)' }}>❌</div>
                      <div>
                        <p className="font-black text-red-400 text-base">Invalid Passcode</p>
                        <p className="text-xs" style={{ color: 'rgba(252,165,165,0.7)' }}>This DO could not be verified</p>
                      </div>
                    </div>
                    <div className="px-6 py-5 space-y-3">
                      <p className="text-sm text-white">This DO could not be verified. Please check the code and try again.</p>
                      <p className="text-sm" style={{ color: '#94a3b8' }}>Contact SugarHathi: <a href="mailto:sugarhathi@gmail.com" className="font-bold" style={{ color: '#fbbf24' }}>sugarhathi@gmail.com</a></p>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* ── TRUSTHATHI TAB ── */}
        {activeTab === 'trust' && (
          <>
            {/* Input Card */}
            <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'rgba(20,45,75,0.85)', border: '1px solid rgba(217,119,6,0.25)', boxShadow: '0 0 40px rgba(217,119,6,0.12), 0 20px 60px rgba(0,0,0,0.4)', backdropFilter: 'blur(12px)' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: 'rgba(217,119,6,0.2)', background: 'rgba(217,119,6,0.06)' }}>
                <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#d97706' }}>TRUSTHATHI VERIFY CODE</p>
                <p className="text-slate-400 text-xs mt-0.5">Enter the 8-character code printed on your TrustHathi Certificate</p>
              </div>
              <div className="px-6 py-6">
                <input
                  type="text"
                  value={trustCode}
                  onChange={(e) => setTrustCode(e?.target?.value?.toUpperCase())}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. R5XJKJEG"
                  maxLength={12}
                  className="w-full px-4 py-4 rounded-xl text-base font-black tracking-[0.2em] uppercase focus:outline-none transition-all placeholder:font-normal placeholder:tracking-normal"
                  style={{ backgroundColor: '#ffffff', color: '#374151', border: `2px solid ${trustCode ? '#d97706' : '#e5e7eb'}`, caretColor: '#d97706' }}
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="text-xs mt-2 ml-1" style={{ color: '#9ca3af' }}>Auto-converts to uppercase · Press Enter to verify</p>
                <button
                  onClick={handleTrustVerify}
                  disabled={trustLoading || !trustCode?.trim()}
                  className="mt-5 w-full py-4 rounded-xl font-black text-base tracking-widest uppercase transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
                  style={{ backgroundColor: trustLoading || !trustCode?.trim() ? 'rgba(217,119,6,0.5)' : '#d97706', color: '#1e4a72', boxShadow: trustCode && !trustLoading ? '0 4px 24px rgba(217,119,6,0.45)' : 'none' }}
                  onMouseEnter={(e) => { if (!trustLoading && trustCode?.trim()) e.currentTarget.style.backgroundColor = '#f59e0b'; }}
                  onMouseLeave={(e) => { if (!trustLoading && trustCode?.trim()) e.currentTarget.style.backgroundColor = '#d97706'; }}
                >
                  {trustLoading ? (<><svg className="animate-spin h-5 w-5" style={{ color: '#1e4a72' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>Verifying…</>) : (<>🛡️ VERIFY CERTIFICATE</>)}
                </button>
              </div>
            </div>

            {/* TrustHathi Result */}
            {trustSearched && trustResult && (
              <div className="w-full max-w-md mt-6 rounded-2xl overflow-hidden shadow-2xl" style={{ animation: trustResult?.found ? 'fadeInUp 0.4s ease both' : 'shakeIn 0.5s ease both', border: trustResult?.found ? '2px solid rgba(34,197,94,0.5)' : '2px solid rgba(239,68,68,0.5)', background: 'rgba(20,45,75,0.9)', backdropFilter: 'blur(12px)' }}>
                {trustResult?.found ? (
                  <>
                    {/* Header */}
                    <div className="px-6 py-4 flex items-center gap-3 border-b" style={{ background: trustResult?.data?.overall_result === 'PASS' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', borderColor: trustResult?.data?.overall_result === 'PASS' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)' }}>
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: trustResult?.data?.overall_result === 'PASS' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)', border: trustResult?.data?.overall_result === 'PASS' ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(239,68,68,0.4)' }}>
                        {trustResult?.data?.overall_result === 'PASS' ? '✅' : '⚠️'}
                      </div>
                      <div>
                        <p className={`font-black text-base ${trustResult?.data?.overall_result === 'PASS' ? 'text-green-400' : 'text-red-400'}`}>
                          {trustResult?.data?.overall_result === 'PASS' ? 'Vehicle Compliance Verified — PASS' : 'Compliance Issues Found — FAIL'}
                        </p>
                        <p className="text-xs" style={{ color: '#94a3b8' }}>TrustHathi Certificate · Verified by SugarHathi</p>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="px-6 py-5 space-y-2">
                      {/* Overall Result Badge */}
                      <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                        <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#94a3b8' }}>Overall Result</span>
                        <span className={`text-xs font-black px-3 py-1 rounded-full ${trustResult?.data?.overall_result === 'PASS' ? 'bg-green-900/40 text-green-300 border border-green-500/40' : 'bg-red-900/40 text-red-300 border border-red-500/40'}`}>
                          {trustResult?.data?.overall_result}
                        </span>
                      </div>

                      {/* Vehicle & Driver */}
                      {[
                        { label: 'Vehicle Number', value: trustResult?.data?.vehicle_number },
                        { label: 'Driver Name', value: trustResult?.data?.driver_name },
                        { label: 'Owner Name', value: trustResult?.data?.rc_owner_name },
                        { label: 'Vehicle Class', value: trustResult?.data?.rc_vh_class_desc },
                      ].map((row, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                          <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#94a3b8' }}>{row.label}</span>
                          <span className="text-sm font-bold text-white text-right max-w-[55%]">{row.value || '—'}</span>
                        </div>
                      ))}

                      {/* Status fields — color coded */}
                      {[
                        { label: 'RC Status', value: trustResult?.data?.rc_status },
                        { label: 'Blacklist Status', value: trustResult?.data?.rc_blacklist_status },
                        { label: 'DL Status', value: trustResult?.data?.dl_status },
                      ].map((row, i) => (                      
                        <div key={i} className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                          <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#94a3b8' }}>{row.label}</span>
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${isGoodStatus(row.value) ? 'bg-green-900/40 text-green-300 border border-green-500/40' : 'bg-red-900/40 text-red-300 border border-red-500/40'}`}>
                            {row.value || 'No Record Available'}
                          </span>
                        </div>
                      ))}

                      {/* Date fields */}
                      {[
                        { label: 'Insurance Upto', value: trustResult?.data?.rc_insurance_upto },
                        { label: 'Fitness Upto', value: trustResult?.data?.rc_fit_upto },
                        { label: 'Permit Valid Upto', value: trustResult?.data?.rc_permit_valid_upto },
                        { label: 'DL Valid Upto', value: trustResult?.data?.dl_tr_val_upto },
                        { label: 'Vehicle Classes', value: trustResult?.data?.dl_covdesc },
                      ].map((row, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                          <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#94a3b8' }}>{row.label}</span>
                          <span className="text-sm font-bold text-white text-right max-w-[55%]">{row.value || 'No Record Available'}</span>
                        </div>
                      ))}

                      {/* Verified At */}
                      <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                        <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#94a3b8' }}>Verified At</span>
                        <span className="text-sm font-bold text-white">{formatDate(trustResult?.data?.created_at)}</span>
                      </div>

                      {/* Fail Reasons Box */}
                      {trustResult?.data?.fail_reasons && trustResult?.data?.fail_reasons?.length > 0 && (
                        <div className="mt-3 p-4 rounded-xl border" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }}>
                          <p className="text-xs font-black text-red-400 mb-2 uppercase tracking-wide">Issues Identified:</p>
                          {trustResult?.data?.fail_reasons?.map((reason, i) => (
                            <p key={i} className="text-xs text-red-300 mb-1">• {reason}</p>
                          ))}
                        </div>
                      )}

                      {/* Disclaimer */}
                      <p className="text-xs pt-2" style={{ color: '#64748b' }}>
                        Point-in-time declaration. Data reflects government records at moment of verification. Powered by MORTH via ULIP.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-6 py-4 flex items-center gap-3 border-b" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.2)' }}>
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)' }}>❌</div>
                      <div>
                        <p className="font-black text-red-400 text-base">Invalid Verify Code</p>
                        <p className="text-xs" style={{ color: 'rgba(252,165,165,0.7)' }}>This certificate could not be verified</p>
                      </div>
                    </div>
                    <div className="px-6 py-5 space-y-3">
                      <p className="text-sm text-white">No certificate found for this code. Please check and try again.</p>
                      <p className="text-sm" style={{ color: '#94a3b8' }}>Contact SugarHathi: <a href="mailto:sugarhathi@gmail.com" className="font-bold" style={{ color: '#fbbf24' }}>sugarhathi@gmail.com</a></p>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        <div className="w-full max-w-md mt-8 flex flex-col sm:flex-row gap-3">
          <button onClick={() => navigate('/sugar-marketplace-dashboard')} className="flex-1 py-4 px-6 rounded-xl text-sm font-bold transition-all text-center shadow-lg active:scale-95" style={{ backgroundColor: '#d97706', color: '#1e4a72', boxShadow: '0 4px 20px rgba(217,119,6,0.35)' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f59e0b'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#d97706'; }}>
            🛒 Browse Sugar Offers
          </button>
          <button onClick={() => navigate('/')} className="flex-1 py-4 px-6 rounded-xl text-sm font-bold transition-all text-center active:scale-95" style={{ backgroundColor: 'transparent', color: '#ffffff', border: '2px solid rgba(255,255,255,0.5)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.9)'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.backgroundColor = 'transparent'; }}>
            🏠 Back to Home
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-6 px-4 text-center border-t mt-auto" style={{ backgroundColor: 'rgba(10,25,41,0.8)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="flex items-center justify-center gap-2 mb-2 cursor-pointer" onClick={() => navigate('/')} title="SugarHathi home">
          <div className="w-6 h-6 rounded overflow-hidden">
            <img src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png" alt="SugarHathi" className="w-full h-full object-contain" />
          </div>
          <span className="text-white font-bold text-sm">SugarHathi</span>
        </div>
        <p className="text-xs" style={{ color: '#64748b' }}>Powered by SugarHathi · India's B2B Sugar Procurement Platform</p>
        <div className="flex items-center justify-center gap-4 mt-3">
          <button onClick={() => navigate('/')} className="text-xs transition-colors" style={{ color: '#64748b' }} onMouseEnter={(e) => e.currentTarget.style.color = '#d97706'} onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}>Home</button>
          <span style={{ color: '#1e3a5f' }}>·</span>
          <button onClick={() => navigate('/sugar-marketplace-dashboard')} className="text-xs transition-colors" style={{ color: '#64748b' }} onMouseEnter={(e) => e.currentTarget.style.color = '#d97706'} onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}>Browse Offers</button>
          <span style={{ color: '#1e3a5f' }}>·</span>
          <button onClick={() => navigate('/login-authentication')} className="text-xs transition-colors" style={{ color: '#64748b' }} onMouseEnter={(e) => e.currentTarget.style.color = '#d97706'} onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}>Sign In</button>
        </div>
      </footer>

      <style>{`
        @keyframes pageFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shakeIn { 0% { opacity: 0; transform: translateX(0); } 20% { opacity: 1; transform: translateX(-8px); } 40% { transform: translateX(8px); } 60% { transform: translateX(-5px); } 80% { transform: translateX(5px); } 100% { opacity: 1; transform: translateX(0); } }
        @keyframes checkPop { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default DoVerifyPage;
